import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { geoEqualEarth, geoPath } from "d3-geo";
import { feature } from "topojson-client";

// [name, lat, lng, isMajor]
const CITIES = [
    ["Hollywood", 34.05, -118.25, true],
    ["NYC", 40.71, -74.0, false],
    ["Toronto", 43.65, -79.38, false],
    ["Park City", 40.65, -111.5, false],
    ["Mexico City", 19.43, -99.13, false],
    ["São Paulo", -23.55, -46.63, false],
    ["Buenos Aires", -34.6, -58.38, false],
    ["Rio", -22.9, -43.21, false],
    ["London", 51.51, -0.13, false],
    ["Paris", 48.85, 2.35, true],
    ["Cannes", 43.55, 7.02, false],
    ["Berlin", 52.52, 13.41, false],
    ["Rotterdam", 51.92, 4.48, false],
    ["Locarno", 46.17, 8.79, false],
    ["Rome", 41.9, 12.5, false],
    ["Venice", 45.44, 12.33, false],
    ["Madrid", 40.42, -3.7, false],
    ["Stockholm", 59.33, 18.07, false],
    ["Prague", 50.08, 14.44, false],
    ["Moscow", 55.76, 37.62, false],
    ["Istanbul", 41.01, 28.98, false],
    ["Tel Aviv", 32.08, 34.78, false],
    ["Cairo", 30.04, 31.24, false],
    ["Lagos", 6.52, 3.38, true],
    ["Johannesburg", -26.2, 28.03, false],
    ["Nairobi", -1.29, 36.82, false],
    ["Marrakech", 31.63, -7.99, false],
    ["Tehran", 35.69, 51.39, false],
    ["Dubai", 25.2, 55.27, false],
    ["Karachi", 24.86, 67.01, false],
    ["Lahore", 31.55, 74.36, false],
    ["Mumbai", 19.07, 72.87, true],
    ["Chennai", 13.08, 80.27, false],
    ["Hyderabad", 17.38, 78.49, false],
    ["Kochi", 9.93, 76.27, false],
    ["Goa", 15.3, 74.12, false],
    ["Dhaka", 23.81, 90.41, false],
    ["Bangkok", 13.76, 100.5, false],
    ["Ho Chi Minh", 10.82, 106.63, false],
    ["Jakarta", -6.21, 106.85, false],
    ["Manila", 14.6, 120.98, false],
    ["Hong Kong", 22.32, 114.17, false],
    ["Shanghai", 31.23, 121.47, true],
    ["Beijing", 39.9, 116.4, false],
    ["Taipei", 25.03, 121.56, false],
    ["Seoul", 37.57, 126.98, true],
    ["Busan", 35.18, 129.08, false],
    ["Tokyo", 35.68, 139.65, true],
    ["Osaka", 34.69, 135.5, false],
    ["Sydney", -33.87, 151.21, false],
    ["Auckland", -36.85, 174.76, false],
];

const VB_W = 1000;
const VB_H = 520;

// fixed projection sized to fit our viewbox
const projection = geoEqualEarth()
    .scale(190)
    .translate([VB_W / 2, VB_H / 2 + 10]);

const pathGen = geoPath(projection);
const projectPoint = ([lng, lat]) => projection([lng, lat]);

export default function WorldMap() {
    const [features, setFeatures] = useState(null);

    useEffect(() => {
        let cancel = false;
        fetch("/world-110m.json")
            .then((r) => r.json())
            .then((topo) => {
                if (cancel) return;
                const geo = feature(topo, topo.objects.countries);
                setFeatures(geo.features);
            })
            .catch(() => {});
        return () => {
            cancel = true;
        };
    }, []);

    const hubArcs = useMemo(() => {
        const majors = CITIES.filter((c) => c[3]);
        const pairs = [];
        for (let i = 0; i < majors.length; i++) {
            pairs.push([majors[i], majors[(i + 1) % majors.length]]);
        }
        pairs.push([majors[0], majors[3]]);
        pairs.push([majors[1], majors[5]]);
        return pairs;
    }, []);

    return (
        <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative w-full"
            data-testid="world-map"
        >
            <svg
                viewBox={`0 0 ${VB_W} ${VB_H}`}
                className="w-full h-auto"
                role="img"
                aria-label="Global cinema industries and festivals represented on Silver Screen Xchange"
            >
                <defs>
                    <radialGradient id="hubGlow" cx="50%" cy="50%" r="50%">
                        <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.95" />
                        <stop offset="55%" stopColor="#E5E4E2" stopOpacity="0.25" />
                        <stop offset="100%" stopColor="#E5E4E2" stopOpacity="0" />
                    </radialGradient>
                </defs>

                {/* Countries */}
                {features &&
                    features.map((f, i) => (
                        <path
                            key={i}
                            d={pathGen(f)}
                            fill="#E5E4E2"
                            fillOpacity="0.08"
                            stroke="#E5E4E2"
                            strokeOpacity="0.35"
                            strokeWidth="0.5"
                        />
                    ))}

                {/* Arcs between major hubs */}
                {features &&
                    hubArcs.map(([a, b], k) => {
                        const pA = projectPoint([a[2], a[1]]);
                        const pB = projectPoint([b[2], b[1]]);
                        if (!pA || !pB) return null;
                        const mx = (pA[0] + pB[0]) / 2;
                        const my =
                            Math.min(pA[1], pB[1]) -
                            Math.abs(pA[0] - pB[0]) * 0.2;
                        return (
                            <path
                                key={k}
                                d={`M${pA[0]},${pA[1]} Q${mx},${my} ${pB[0]},${pB[1]}`}
                                fill="none"
                                stroke="#E5E4E2"
                                strokeOpacity="0.22"
                                strokeWidth="0.8"
                                strokeDasharray="2 4"
                            />
                        );
                    })}

                {/* City markers */}
                {features &&
                    CITIES.map(([name, lat, lng, major]) => {
                        const p = projectPoint([lng, lat]);
                        if (!p) return null;
                        const [x, y] = p;
                        if (major) {
                            return (
                                <g key={name}>
                                    <circle cx={x} cy={y} r="22" fill="url(#hubGlow)">
                                        <animate
                                            attributeName="r"
                                            values="14;28;14"
                                            dur="3.4s"
                                            repeatCount="indefinite"
                                        />
                                        <animate
                                            attributeName="opacity"
                                            values="1;0.3;1"
                                            dur="3.4s"
                                            repeatCount="indefinite"
                                        />
                                    </circle>
                                    <circle cx={x} cy={y} r="4" fill="#FFFFFF" />
                                    <text
                                        x={x + 9}
                                        y={y - 7}
                                        fontSize="10"
                                        fill="#FFFFFF"
                                        fontFamily="Inter, sans-serif"
                                        letterSpacing="1.3"
                                        fontWeight="500"
                                    >
                                        {name.toUpperCase()}
                                    </text>
                                </g>
                            );
                        }
                        return (
                            <circle
                                key={name}
                                cx={x}
                                cy={y}
                                r="2.6"
                                fill="#F5F5F5"
                                opacity="0.9"
                            >
                                <title>{name}</title>
                            </circle>
                        );
                    })}

                {!features && (
                    <text
                        x={VB_W / 2}
                        y={VB_H / 2}
                        fill="#A8A7A5"
                        fontSize="14"
                        textAnchor="middle"
                        fontFamily="Inter, sans-serif"
                    >
                        Loading world map…
                    </text>
                )}
            </svg>
        </motion.div>
    );
}
